import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import PlaidConnect from "../components/PlaidConnect";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { RefreshCw, ArrowUpRight, ArrowDownRight, Scale, PiggyBank, Info, Search, ChevronRight, Wallet } from "lucide-react";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, CartesianGrid,
} from "recharts"; // eslint-disable-line no-unused-vars
import { motion } from "framer-motion";
import ExplainerCards from "../components/ExplainerCards";
import TransactionDrawer from "../components/TransactionDrawer";
import CategoryDrillDrawer from "../components/CategoryDrillDrawer";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import CountUp from "../components/CountUp";
import confetti from "canvas-confetti";
import LendScoreGauge from "../components/LendScoreGauge";
import PortfolioAndDebts from "../components/PortfolioAndDebts";
import CashFlowSankey from "../components/CashFlowSankey";
import NetWorthTimeline from "../components/NetWorthTimeline"; // eslint-disable-line no-unused-vars
import ManualAssets from "../components/ManualAssets";
import TipsRotator from "../components/TipsRotator";
import ThreeDPie from "../components/ThreeDPie";
import EndCappedLineChart from "../components/EndCappedLineChart";
import PredictionsCard from "../components/PredictionsCard";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

// Category → hex color map. Each of the 25 app categories gets a distinct hue
// in the FlowMint palette (mint + coral + peach + violet accents).
const CATEGORY_COLOR_MAP = {
    // Meta & legacy
    transfer: "#0AFFB0",       // neon mint (default largest)
    other: "#94A3B8",          // cool slate

    // Income & Adjustments — mint family
    income: "#00D68F",         // neon green
    salary: "#0AFFB0",         // neon mint
    freelance: "#34E0B8",      // mint aqua
    side_income: "#5FEBCF",    // pale mint aqua
    reimbursement: "#7DF3D5",  // soft mint
    cashback: "#A7F9E1",       // pastel mint
    refunded: "#00D68F",       // neon green

    // Lifestyle & Daily — sunset + violet band
    groceries: "#FFB84D",      // amber
    dining: "#FF6B6B",         // coral red
    shopping: "#B36CFF",       // electric violet
    entertainment: "#FF3EA5",  // hot fuchsia
    subscriptions: "#F13985",  // deep fuchsia
    personal_care: "#FF9BC8",  // pink
    transportation: "#FF7A45", // sunset orange
    medical: "#38BDF8",        // sky
    healthcare: "#38BDF8",     // sky (alias)

    // Housing & Utilities — deeper accents
    housing: "#E85D6F",        // rose
    rent: "#F471A0",           // pink rose
    mortgage: "#C026D3",       // magenta
    home_insurance: "#8B5CF6", // violet
    property_tax: "#6366F1",   // indigo
    utilities: "#7DD3FC",      // ice blue

    // Financial & Protection — cyan + violet
    insurance: "#22D3EE",      // cyan
    investment: "#0891B2",     // deep cyan
    savings: "#A855F7",        // rich violet
    debt_payment: "#FBBF24",   // amber gold
    loan_repayment: "#F59E0B", // deep amber
};

// Positional fallback palette — neon-forward Gen-Z stops
const CAT_COLORS_FALLBACK = ["#0AFFB0", "#B36CFF", "#FF3EA5", "#FF7A45", "#22D3EE", "#FBBF24", "#F13985", "#8B5CF6", "#38BDF8"];

// Resolve a color for a category key (falls back to positional palette).
const colorFor = (key, idx = 0) =>
    CATEGORY_COLOR_MAP[key] || CAT_COLORS_FALLBACK[idx % CAT_COLORS_FALLBACK.length];

// Assign UNIQUE colors across a list of categories. If two categories map to
// the same color in CATEGORY_COLOR_MAP, the second one falls through to the
// next unused stop from CAT_COLORS_FALLBACK (extended with a broader neon set
// so we never repeat within a pie).
const UNIQUE_PALETTE = [
    "#0AFFB0", // neon mint
    "#B36CFF", // electric violet
    "#FF3EA5", // hot fuchsia
    "#FF7A45", // sunset orange
    "#22D3EE", // cyan
    "#FBBF24", // amber gold
    "#F13985", // deep fuchsia
    "#8B5CF6", // rich violet
    "#38BDF8", // sky
    "#A7F9E1", // pastel mint
    "#F471A0", // pink rose
    "#00D68F", // deep neon green
    "#C026D3", // magenta
    "#6366F1", // indigo
    "#FF9BC8", // pink
];
const assignUniqueColors = (categories) => {
    const used = new Set();
    const out = {};
    for (let i = 0; i < categories.length; i++) {
        const key = categories[i].category;
        const preferred = CATEGORY_COLOR_MAP[key];
        let color = preferred && !used.has(preferred) ? preferred : null;
        if (!color) {
            color = UNIQUE_PALETTE.find((c) => !used.has(c)) || UNIQUE_PALETTE[i % UNIQUE_PALETTE.length];
        }
        used.add(color);
        out[key] = color;
    }
    return out;
};

const HERO_VARIANTS = ["hero_var_a", "hero_var_b", "hero_var_c", "hero_var_d"]; // eslint-disable-line no-unused-vars

const RANGES = [
    { key: "1W", days: 7 },
    { key: "2W", days: 14 },
    { key: "1M", days: 30 },
    { key: "3M", days: 90 },
    { key: "6M", days: 180 },
];

const catLabel = (lang, key) => t(lang, `cat_${key}`);

const MetricCard = ({ eyebrow, subEyebrow, amount, icon: Icon, iconBg, iconColor, valueColor, testId, cardBg, cardText }) => (
    <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
    >
        <Card
            className={`hover-lift rounded-2xl shadow-sm ${cardBg ? "border-none" : "bg-[hsl(var(--card))] border-[hsl(var(--border))]"}`}
            style={cardBg ? { backgroundColor: cardBg, color: cardText || "#FFFFFF" } : undefined}
            data-testid={testId}
        >
            <CardContent className="p-6">
                <div className="flex items-start justify-between mb-1">
                    <div className={`text-[11px] font-bold tracking-[0.2em] uppercase pb-1 border-b ${cardBg ? "border-white/25 text-white/85" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>
                        <span>{eyebrow}</span>
                        {subEyebrow && <span className="ml-1 opacity-80">· {subEyebrow}</span>}
                    </div>
                    <div className={`w-8 h-8 rounded-full grid place-items-center ${iconBg}`}>
                        <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
                    </div>
                </div>
                <div className={`mt-6 font-display font-extrabold text-4xl md:text-[44px] leading-none tracking-tight tabular-nums ${cardBg ? "" : valueColor}`}>
                    <CountUp value={amount} format={fmt} />
                </div>
            </CardContent>
        </Card>
    </motion.div>
);

const Dashboard = () => {
    const { user, language } = useApp();
    const [data, setData] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [status, setStatus] = useState({ connected: false });
    const [syncing, setSyncing] = useState(false);
    const [range, setRange] = useState("1M");
    const [explainerOpen, setExplainerOpen] = useState(false);
    const [txQuery, setTxQuery] = useState("");
    const [txLimit, setTxLimit] = useState(10);
    const [selectedTx, setSelectedTx] = useState(null);
    const [drillCategory, setDrillCategory] = useState(null); // category key user clicked in the pie legend
    const [heroKey] = useState(() => HERO_VARIANTS[Math.floor(Math.random() * HERO_VARIANTS.length)]); // eslint-disable-line no-unused-vars
    const [lendScore, setLendScore] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        // Auto-open onboarding on first Dashboard visit
        if (typeof window !== "undefined") {
            const seen = window.localStorage.getItem("vlg_seen_onboarding");
            if (!seen) {
                setExplainerOpen(true);
                window.localStorage.setItem("vlg_seen_onboarding", "1");
            }
            // Celebrate with confetti on fresh login
            try {
                if (window.sessionStorage.getItem("vlg_just_logged_in") === "1") {
                    window.sessionStorage.removeItem("vlg_just_logged_in");
                    setTimeout(() => {
                        const colors = ["#7DD3B0", "#F9C77E", "#A75A22", "#0D1E2E", "#FFFFFF"];
                        confetti({
                            particleCount: 90,
                            spread: 70,
                            startVelocity: 45,
                            origin: { x: 0.15, y: 0.75 },
                            colors,
                        });
                        confetti({
                            particleCount: 90,
                            spread: 70,
                            startVelocity: 45,
                            origin: { x: 0.85, y: 0.75 },
                            colors,
                        });
                        setTimeout(() => {
                            confetti({
                                particleCount: 60,
                                spread: 120,
                                startVelocity: 35,
                                gravity: 0.9,
                                scalar: 0.9,
                                origin: { x: 0.5, y: 0.2 },
                                colors,
                            });
                        }, 220);
                    }, 300);
                }
            } catch (e) {}
        }
    }, []);

    const load = useCallback(async () => {
        const [d, s, tx] = await Promise.all([
            api.get("/dashboard"),
            api.get("/plaid/status"),
            api.get("/transactions", { params: { limit: 1000 } }),
        ]);
        setData(d.data);
        setStatus(s.data);
        setTransactions(tx.data);
        // Fetch LendScore in the background (proactive, zero-click)
        api.get("/insights").then((r) => setLendScore(r.data?.health_score ?? 0)).catch(() => {});
    }, []);

    useEffect(() => { load(); }, [load]);

    // Overdraft morning alert: once per user per day, if predictions detect
    // an upcoming balance dip. Shows an in-app toast + bumps the notification
    // bell via the /api/notifications endpoint.
    useEffect(() => {
        if (!status.connected) return;
        const todayKey = new Date().toISOString().slice(0, 10);
        const dismissKey = `vlg_overdraft_alert_${todayKey}`;
        if (localStorage.getItem(dismissKey)) return;
        let cancelled = false;
        api.get("/predictions")
            .then((r) => {
                if (cancelled || !r.data?.overdraft_risk) return;
                const upcoming = (r.data.upcoming_bills || [])
                    .sort((a, b) => (a.expected_date || "").localeCompare(b.expected_date || ""))[0];
                const merchant = upcoming?.merchant || (language === "es" ? "un cargo próximo" : "an upcoming charge");
                toast.warning(
                    language === "es" ? "Riesgo de sobregiro" : "Overdraft risk ahead",
                    {
                        description: language === "es"
                            ? `Tu balance podría caer bajo cero antes de tu próximo ingreso. Considera mover fondos o pausar ${merchant}.`
                            : `Your balance may dip below zero before your next paycheck. Consider moving funds or pausing ${merchant}.`,
                        duration: 8000,
                        id: "overdraft-morning",
                        style: { borderLeft: "4px solid #FF3EA5" },
                        action: {
                            label: language === "es" ? "Ver plan" : "See plan",
                            onClick: () => { window.location.href = "/action-plan"; },
                        },
                    }
                );
                localStorage.setItem(dismissKey, "1");
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [status.connected, language]);

    const sync = async () => {
        setSyncing(true);
        try { await api.post("/plaid/sync"); await load(); } finally { setSyncing(false); }
    };

    // Build Income vs Expenses series by aggregating daily
    const chartSeries = useMemo(() => {
        const days = RANGES.find((r) => r.key === range).days;
        const today = new Date();
        const start = new Date(today);
        start.setDate(today.getDate() - days + 1);
        const buckets = {};
        for (let i = 0; i < days; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            const key = d.toISOString().slice(0, 10);
            buckets[key] = { date: key, income: 0, expenses: 0 };
        }
        transactions.forEach((tx) => {
            if (!tx?.date) return;
            const k = String(tx.date).slice(0, 10);
            if (!(k in buckets)) return;
            const amt = Number(tx.amount || 0);
            if (amt > 0) buckets[k].expenses += amt;
            else buckets[k].income += -amt;
        });
        return Object.values(buckets);
    }, [transactions, range]);

    // Cumulative net cash flow series for the end-capped chart.
    // Baseline = day 1 running total, Goal = last-day running total.
    const cashflowSeries = useMemo(() => {
        let running = 0;
        return chartSeries.map((row) => {
            running += (row.income || 0) - (row.expenses || 0);
            const d = new Date(row.date + "T00:00:00");
            return {
                label: d.toLocaleString("en-US", { month: "short", day: "numeric" }),
                fullDate: row.date,
                value: running,
                income: row.income,
                expenses: row.expenses,
            };
        });
    }, [chartSeries]);

    const now = new Date();
    const monthLabel = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

    // Build a 30-day rolling spending-by-category summary from the transactions
    // list, used when `data.spending_by_category` (current month only) is empty.
    const rolling30Categories = useMemo(() => {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 30);
        const totals = {};
        for (const tx of transactions) {
            const amt = Number(tx.amount || 0);
            if (amt <= 0) continue; // expenses only (Plaid: positive = outflow)
            const d = tx.date ? new Date(tx.date) : null;
            if (!d || d < cutoff) continue;
            const cat = tx.app_category || "other";
            totals[cat] = (totals[cat] || 0) + amt;
        }
        return Object.entries(totals)
            .map(([category, amount]) => ({ category, amount: Math.round(amount * 100) / 100 }))
            .sort((a, b) => b.amount - a.amount);
    }, [transactions]);

    if (!data) {
        return (
            <AppLayout title={t(language, "nav_dashboard")}>
                <div className="text-[hsl(var(--muted-foreground))] text-sm">Loading…</div>
            </AppLayout>
        );
    }

    const netWorth = data.total_balance;
    // Fall back to a rolling 30-day window when the current month has no
    // transactions (e.g. early-month users or seeded Plaid Sandbox data
    // that lives in a previous month). Users see real numbers instead of $0.
    const trend = data.trend_30d || [];
    const rolling30Income = trend.reduce((s, d) => s + Math.max(0, d.net || 0), 0);
    const rolling30Expenses = trend.reduce((s, d) => s + Math.max(0, -(d.net || 0)), 0);
    const useRolling = (data.monthly_income || 0) === 0 && (data.monthly_expenses || 0) === 0 && trend.length > 0;
    const income = useRolling ? rolling30Income : data.monthly_income;
    const expenses = useRolling ? rolling30Expenses : data.monthly_expenses;
    const cashFlow = useRolling ? (rolling30Income - rolling30Expenses) : data.cash_flow;
    const windowLabel = useRolling
        ? (language === "es" ? "ÚLTIMOS 30 DÍAS" : "LAST 30 DAYS")
        : (language === "es" ? "ESTE MES" : "THIS MONTH");

    return (
        <AppLayout title={t(language, "nav_dashboard")}>
            {/* Header — inline (non-sticky) per product spec */}
            <div
                className="-mx-4 md:-mx-10 px-4 md:px-10 mb-8 py-4 md:py-6"
                data-testid="dashboard-sticky-header"
            >
                <div className="flex flex-col items-center gap-4">
                    <div className="text-center">
                        <div className="flex items-center justify-center gap-3 mb-2 md:mb-3">
                            <span className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase border-t border-[hsl(var(--border))] pt-1">
                                {language === "es" ? "RESUMEN" : "OVERVIEW"} · {monthLabel}
                            </span>
                        </div>
                        <h1 className="font-display font-extrabold text-[hsl(var(--foreground))] text-3xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.02em]">
                            {language === "es" ? "Tu foto financiera" : "Your financial snapshot"}
                        </h1>
                        <TipsRotator />
                    </div>
                    <div className="flex items-center justify-center gap-3 flex-wrap">
                    <Button
                        data-testid="how-it-works-button"
                        onClick={() => setExplainerOpen(true)}
                        variant="outline"
                        className="pill gap-2 border-[hsl(var(--border))]"
                    >
                        <Info className="w-4 h-4" />
                        {language === "es" ? "Cómo funciona" : "How it works"}
                    </Button>
                    {status.connected ? (
                        <Button data-testid="sync-button" onClick={sync} disabled={syncing} variant="outline" className="pill gap-2">
                            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
                            {t(language, "sync")}
                        </Button>
                    ) : null}
                </div>
                </div>
            </div>

            {!status.connected && (
                <Card className="mb-8 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-3xl border-none overflow-hidden relative">
                    <CardContent className="p-8 md:p-10 flex items-center justify-between gap-6 flex-wrap">
                        <div className="max-w-xl">
                            <div className="text-xs tracking-[0.28em] font-bold uppercase opacity-70">{t(language, "connect_bank")}</div>
                            <div className="font-display text-3xl md:text-4xl font-bold mt-3">{t(language, "connect_bank_desc")}</div>
                        </div>
                        <PlaidConnect onConnected={load} />
                    </CardContent>
                </Card>
            )}

            {/* Metric cards row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
                <MetricCard
                    testId="metric-income"
                    eyebrow={language === "es" ? "INGRESOS" : "INCOME"}
                    subEyebrow={windowLabel}
                    amount={income}
                    icon={ArrowUpRight}
                    iconBg="bg-emerald-100"
                    iconColor="text-emerald-600"
                    valueColor="text-emerald-600"
                />
                <MetricCard
                    testId="metric-expenses"
                    eyebrow={language === "es" ? "GASTOS" : "EXPENSES"}
                    subEyebrow={windowLabel}
                    amount={expenses}
                    icon={ArrowDownRight}
                    iconBg="bg-red-100"
                    iconColor="text-red-500"
                    valueColor="text-red-500"
                />
                <MetricCard
                    testId="metric-cashflow"
                    eyebrow={language === "es" ? "FLUJO DE CAJA" : "CASH FLOW"}
                    subEyebrow={windowLabel}
                    amount={cashFlow}
                    icon={PiggyBank}
                    iconBg={cashFlow >= 0 ? "bg-emerald-100 dark:bg-emerald-500/15" : "bg-red-100 dark:bg-red-500/15"}
                    iconColor={cashFlow >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500"}
                    valueColor={cashFlow >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500"}
                />
                <MetricCard
                    testId="metric-networth"
                    eyebrow={language === "es" ? "PATRIMONIO" : "NET WORTH"}
                    amount={netWorth}
                    icon={Scale}
                    iconBg="bg-[hsl(var(--foreground))]/5"
                    iconColor="text-[hsl(var(--foreground))]"
                    valueColor="text-[hsl(var(--foreground))]"
                />
            </div>

            {/* LendScore widget — always visible, auto-computed, tap to see breakdown */}
            <motion.button
                type="button"
                onClick={() => navigate("/insights")}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
                whileHover={{ y: -2 }}
                data-testid="lendscore-widget"
                className="mt-6 w-full text-left rounded-3xl overflow-hidden shadow-2xl border border-white/10 relative group focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/60"
                style={{
                    background: "linear-gradient(135deg, #0A0F1F 0%, #14173A 45%, #2A1454 100%)",
                }}
            >
                <div className="absolute inset-0 opacity-70 pointer-events-none" style={{
                    background: "radial-gradient(circle at 15% 25%, rgba(10,255,176,0.35) 0%, transparent 55%), radial-gradient(circle at 85% 75%, rgba(179,108,255,0.32) 0%, transparent 55%)",
                }} />
                <div className="relative p-6 md:p-8 grid grid-cols-1 md:grid-cols-[auto,1fr,auto] gap-6 items-center">
                    <LendScoreGauge score={lendScore ?? 0} language={language} size={200} />
                    <div className="text-white">
                        <div className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{ color: "#0AFFB0" }}>
                            {t(language, "lendscore_title")}
                        </div>
                        <div className="font-display font-extrabold text-2xl md:text-3xl tracking-tight mt-1 text-white drop-shadow">
                            {t(language, "lendscore_sub")}
                        </div>
                        <p className="mt-3 text-sm md:text-base text-white/90 leading-relaxed max-w-2xl">
                            {t(language, "lendscore_desc")}
                        </p>
                    </div>
                    <div className="hidden md:flex items-center gap-2 text-sm font-semibold text-white group-hover:text-[#0AFFB0] transition-colors">
                        {t(language, "lendscore_view")}
                        <ChevronRight className="w-5 h-5" />
                    </div>
                </div>
            </motion.button>

            <PortfolioAndDebts />

            {/* Sankey cash-flow — the "where did my money go" visual borrowed
                from Monarch, wired to /api/planning/sankey. */}
            <div className="mt-6">
                <CashFlowSankey />
            </div>

            {/* Manual assets — moved from Settings so users can update net-worth-affecting items in one place */}
            <Card className="mt-6 rounded-3xl border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm" data-testid="dashboard-manual-assets">
                <CardContent className="p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-9 h-9 rounded-xl bg-[hsl(var(--secondary))] grid place-items-center">
                            <Wallet className="w-4 h-4 text-[hsl(var(--primary))]" />
                        </div>
                        <div>
                            <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase">
                                {language === "es" ? "PATRIMONIO" : "NET WORTH"}
                            </div>
                            <div className="font-display font-bold text-xl md:text-2xl tracking-tight">
                                {language === "es" ? "Activos manuales" : "Manual assets"}
                            </div>
                        </div>
                    </div>
                    <ManualAssets />
                </CardContent>
            </Card>

            {/* Predictive analytics — upcoming bills + 30-day balance forecast */}
            <div className="mt-6">
                <PredictionsCard connected={status.connected} />
            </div>

            {/* Charts row */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
                <Card className="rounded-3xl border-[hsl(var(--border))] bg-[hsl(var(--card))] lg:col-span-2 shadow-sm" data-testid="chart-income-expenses">
                    <CardContent className="p-6 md:p-8">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
                            <div>
                                <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase mb-2">
                                    {language === "es" ? "PERIODO SELECCIONADO" : "SELECTED PERIOD"}
                                </div>
                                <div className="font-display font-bold text-2xl md:text-3xl tracking-tight">
                                    {language === "es" ? "Flujo neto acumulado" : "Cumulative net cash flow"}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 bg-[hsl(var(--secondary))]/50 rounded-full p-1 self-start overflow-x-auto max-w-full">
                                {RANGES.map((r) => (
                                    <button
                                        key={r.key}
                                        data-testid={`range-${r.key}`}
                                        onClick={() => setRange(r.key)}
                                        className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-all whitespace-nowrap ${
                                            range === r.key
                                                ? "bg-[hsl(var(--foreground))] text-[hsl(var(--background))]"
                                                : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                                        }`}
                                    >
                                        {r.key}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <EndCappedLineChart
                            data={cashflowSeries}
                            valueKey="value"
                            height={240}
                            baselineLabel={language === "es" ? "INICIO" : "BASELINE"}
                            goalLabel={language === "es" ? "ACTUAL" : "CURRENT"}
                            baselineColor="#B36CFF"
                            goalColor={(cashflowSeries[cashflowSeries.length - 1]?.value ?? 0) >= 0 ? "#0AFFB0" : "#FF3EA5"}
                            lineColor={(cashflowSeries[cashflowSeries.length - 1]?.value ?? 0) >= 0 ? "#0AFFB0" : "#FF3EA5"}
                            formatBadge={(n) => {
                                const abs = Math.abs(n);
                                if (abs >= 1000) return `${n < 0 ? "-" : ""}$${(abs / 1000).toFixed(1)}k`;
                                return `${n < 0 ? "-" : ""}$${Math.round(abs)}`;
                            }}
                            yTickFormatter={(n) => {
                                const abs = Math.abs(n);
                                if (abs >= 1000) return `${n < 0 ? "-" : ""}$${(abs / 1000).toFixed(0)}k`;
                                return `${n < 0 ? "-" : ""}$${Math.round(abs)}`;
                            }}
                            tooltipFormatter={(v) => fmt(v)}
                            tooltipLabelFormatter={(l) => l}
                            dataTestId="cashflow-endcapped-chart"
                        />
                    </CardContent>
                </Card>

                <Card className="rounded-3xl border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm" data-testid="chart-category">
                    <CardContent className="p-6 md:p-8">
                        <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase mb-2">
                            {windowLabel}
                        </div>
                        <div className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-1">
                            {language === "es" ? "Por categoría" : "By category"}
                        </div>
                        {(() => {
                            // Prefer current-month spending; fall back to the last-30-days rollup
                            // so users always see a live pie instead of "No data yet".
                            const source = (data.spending_by_category && data.spending_by_category.length > 0)
                                ? data.spending_by_category
                                : rolling30Categories;
                            const isFallback = source === rolling30Categories && (data.spending_by_category || []).length === 0;
                            return source.length === 0 ? (
                                <div className="text-sm text-[hsl(var(--muted-foreground))] py-8">{t(language, "no_data")}</div>
                            ) : (
                                (() => {
                                    // Compute a UNIQUE color per category so the pie never repeats a slice color.
                                    const uniq = assignUniqueColors(source);
                                    return (
                                <>
                                    <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mb-4">
                                        {isFallback
                                            ? (language === "es" ? "Últimos 30 días" : "Last 30 days")
                                            : (language === "es" ? "Este mes" : "This month")}
                                    </div>
                                    <motion.div
                                        key={language + range}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                                    >
                                        {/* Big responsive 3D pie */}
                                        <div className="w-full max-w-[460px] mx-auto lg:max-w-none">
                                            <ThreeDPie
                                                data={source.map((c, i) => ({
                                                    label: catLabel(language, c.category),
                                                    value: c.amount,
                                                    color: uniq[c.category] || colorFor(c.category, i),
                                                    _raw: c,
                                                }))}
                                                width={460}
                                                height={280}
                                                depth={38}
                                                explodePx={12}
                                                tilt={0.5}
                                                className="w-full h-auto"
                                                onSliceClick={(slice) => {
                                                    const total = source.reduce((s, x) => s + (x.amount || 0), 0) || 1;
                                                    const pct = ((slice.value / total) * 100).toFixed(1);
                                                    toast(slice.label, {
                                                        description: language === "es"
                                                            ? `${fmt(slice.value)} · ${pct}% de tus gastos`
                                                            : `${fmt(slice.value)} · ${pct}% of spending`,
                                                        duration: 2500,
                                                        style: { borderLeft: `4px solid ${slice.color}` },
                                                    });
                                                    if (slice._raw && slice._raw.category) {
                                                        setDrillCategory(slice._raw.category);
                                                    }
                                                }}
                                            />
                                        </div>

                                        {/* Neon chip legend — each chip tinted with its slice color */}
                                        <div className="mt-5 flex flex-wrap gap-2" data-testid="pie-legend">
                                            {source.slice(0, 8).map((c, i) => {
                                                const color = uniq[c.category] || colorFor(c.category, i);
                                                return (
                                                    <motion.button
                                                        key={c.category}
                                                        type="button"
                                                        onClick={() => setDrillCategory(c.category)}
                                                        initial={{ opacity: 0, y: 6 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.3, delay: 0.15 + i * 0.05 }}
                                                        className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 border transition-all hover:-translate-y-0.5 cursor-pointer"
                                                        style={{
                                                            background: `${color}18`,
                                                            borderColor: `${color}55`,
                                                            color: color,
                                                        }}
                                                        data-testid={`pie-legend-chip-${c.category}`}
                                                    >
                                                        <span className="w-2 h-2 rounded-full" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
                                                        <span className="text-[13px] font-semibold text-[hsl(var(--foreground))]">
                                                            {catLabel(language, c.category)}
                                                        </span>
                                                        <span className="text-[13px] font-bold tabular-nums" style={{ color }}>
                                                            <CountUp value={c.amount} format={fmt} />
                                                        </span>
                                                    </motion.button>
                                                );
                                            })}
                                        </div>
                                    </motion.div>
                                </>
                                    );
                                })()
                            );
                        })()}
                    </CardContent>
                </Card>
            </div>

            {/* Transactions section */}
            <div className="mt-6">
                <Card data-testid="dashboard-transactions" className="rounded-3xl border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
                    <CardContent className="p-6 md:p-8">
                        <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
                            <div>
                                <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase mb-1">
                                    {language === "es" ? "MOVIMIENTOS · RECIENTES" : "MOVEMENTS · RECENT"}
                                </div>
                                <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
                                    {language === "es" ? "Tus transacciones" : "Your transactions"}
                                </h2>
                            </div>
                            <div className="relative w-full sm:w-64">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                                <Input
                                    data-testid="dashboard-tx-search"
                                    placeholder={language === "es" ? "Buscar comercio..." : "Search merchant..."}
                                    className="pl-10 pill h-10 bg-[hsl(var(--secondary))]/40"
                                    value={txQuery}
                                    onChange={(e) => setTxQuery(e.target.value)}
                                />
                            </div>
                        </div>
                        {(() => {
                            const filtered = transactions.filter((tx) => {
                                if (!txQuery) return true;
                                const name = (tx.merchant_name || tx.name || "").toLowerCase();
                                return name.includes(txQuery.toLowerCase());
                            });
                            if (filtered.length === 0) {
                                return <div className="py-8 text-center text-sm text-[hsl(var(--muted-foreground))]">{t(language, "no_data")}</div>;
                            }
                            return (
                                <>
                                    <div className="divide-y divide-[hsl(var(--border))]">
                                        {filtered.slice(0, txLimit).map((tx) => (
                                            <button
                                                key={tx.transaction_id}
                                                data-testid="dashboard-transaction-row"
                                                onClick={() => setSelectedTx(tx)}
                                                className="w-full flex items-center justify-between py-3 text-left hover:bg-[hsl(var(--secondary))]/40 rounded-xl px-2 -mx-2 transition-colors"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium text-[hsl(var(--foreground))] truncate">
                                                        {tx.merchant_name || tx.name}
                                                    </div>
                                                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 flex items-center gap-2">
                                                        <span>{tx.date?.slice(0, 10)}</span>
                                                        <Badge variant="outline" className="text-[10px] font-medium tracking-wide">
                                                            {t(language, `cat_${tx.app_category || "other"}`)}
                                                        </Badge>
                                                    </div>
                                                </div>
                                                <div className={`font-semibold text-base tabular-nums ml-3 ${tx.amount < 0 ? "text-emerald-600" : "text-[hsl(var(--foreground))]"}`}>
                                                    {tx.amount < 0 ? "+" : "-"}{fmt(Math.abs(tx.amount))}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                    {filtered.length > txLimit && (
                                        <div className="pt-4 mt-2 border-t border-[hsl(var(--border))] flex justify-center">
                                            <button
                                                data-testid="dashboard-tx-load-more"
                                                onClick={() => setTxLimit((n) => n + 10)}
                                                className="pill text-xs font-semibold px-4 py-2 bg-[hsl(var(--secondary))]/60 hover:bg-[hsl(var(--secondary))] transition-colors"
                                            >
                                                {language === "es" ? "Ver más" : "Load more"} · {filtered.length - txLimit} {language === "es" ? "restantes" : "left"}
                                            </button>
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </CardContent>
                </Card>
            </div>

            <ExplainerCards open={explainerOpen} onClose={() => setExplainerOpen(false)} />
            <TransactionDrawer
                open={!!selectedTx}
                onOpenChange={(o) => !o && setSelectedTx(null)}
                transaction={selectedTx}
            />
            <CategoryDrillDrawer
                open={!!drillCategory}
                onOpenChange={(o) => !o && setDrillCategory(null)}
                category={drillCategory}
                transactions={transactions}
                color={drillCategory ? (assignUniqueColors(
                    (data.spending_by_category && data.spending_by_category.length > 0)
                        ? data.spending_by_category
                        : rolling30Categories
                )[drillCategory] || colorFor(drillCategory)) : "#0AFFB0"}
            />
        </AppLayout>
    );
};

export default Dashboard;
